import Notification from '../entities/notification/Notification'
import { store } from '../services/store'
import { setNotification } from '../services/store/actions/notifications/NotificationsAction'

import { parseEnvelope } from './envelope'
import { resolveLocalizable } from './notificationCatalog'
import { IntlService } from '../services/intl/IntlService'
import { PushDedup } from './dedup'

export interface PushMessage {
  id: string
  text: string
  data?: Record<string, string>
}

export interface HandleOptions {
  /**
   * Foreground/web pushes carry the server body, so fall back to it when the
   * envelope can't be rendered. The background-tap path has no server body in the
   * intent, so it passes false and the banner is skipped if unrenderable.
   */
  bodyFallback: boolean
}

/**
 * Single consumer for every inbound push: foreground custom event, web onMessage,
 * and background-tap. Parses the typed envelope, dedups on hash/id, renders the
 * localized text, and dispatches the in-app banner.
 *
 * `onConsumed` runs only when a banner is actually shown — used by the foreground
 * and tap paths to trigger their dashboard-refresh side effects.
 */
export function handlePushMessage(
  msg: PushMessage,
  options: HandleOptions,
  onConsumed?: () => void
): void {
  const env = parseEnvelope(msg.data)
  const key = env?.hash ?? msg.id

  if (PushDedup.isDuplicate(key)) {
    console.log('[Push] duplicate suppressed', key)
    return
  }

  const localizable = resolveLocalizable(env)
  const fallbackBody = options.bodyFallback ? msg.text : ''
  // `text` is the immediate render (localized now, or the server body). When
  // localizable, the toast re-formats reactively from event + params on language
  // change; `text` is kept as the pre-render / fallback.
  const text = localizable
    ? IntlService.formatMessage(localizable.descriptor, localizable.values)
    : fallbackBody

  if (!text.trim()) {
    console.log('[Push] no renderable text; skipping banner', { event: env?.event })
    return
  }

  store.dispatch(
    setNotification(
      new Notification({
        id: key,
        text,
        event: env?.event,
        group: env?.group,
        severity: env?.severity,
        receiverType: env?.receiverType,
        hash: env?.hash,
        actionUrl: env?.actionUrl,
        params: env?.params,
        localizable: !!localizable,
      })
    )
  )

  onConsumed?.()
}
